import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit2, Trash2, LayoutTemplate, 
  Facebook, Instagram, Twitter, Linkedin, 
  X, Image as ImageIcon, Save, Copy
} from 'lucide-react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const SocialMediaTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    platforms: ['facebook'],
    category: 'Promotions',
    mediaUrl: ''
  });

  const categories = ['All', 'Promotions', 'Announcements', 'Events', 'Menu Updates'];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // Mock data
      setTimeout(() => {
        setTemplates([
          {
            id: '1',
            name: 'Weekend Happy Hour',
            content: 'Kick off your weekend right! 🍻 Join us for Happy Hour today from 4 PM to 7 PM. Enjoy 50% off all appetizers and signature cocktails. See you there! ✨',
            platforms: ['facebook', 'instagram'],
            category: 'Promotions',
            createdAt: new Date(Date.now() - 86400000 * 5).toISOString(),
            mediaUrl: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=200'
          },
          {
            id: '2',
            name: 'New Menu Item Alert',
            content: 'Craving something new? Try our latest chef special! Made with locally sourced ingredients and a whole lot of love. Available starting this week. 🍽️',
            platforms: ['facebook', 'instagram', 'twitter'],
            category: 'Menu Updates',
            createdAt: new Date(Date.now() - 86400000 * 12).toISOString(),
            mediaUrl: null
          },
          {
            id: '3',
            name: 'Holiday Closure',
            content: 'Please note that we will be closed on [Holiday Date] to allow our team to celebrate with their families. We will resume normal hours on [Return Date]. Thank you for your continued support! ❤️',
            platforms: ['facebook', 'linkedin'],
            category: 'Announcements',
            createdAt: new Date(Date.now() - 86400000 * 30).toISOString(),
            mediaUrl: null
          }
        ]);
        setLoading(false);
      }, 600);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const openModal = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        content: template.content,
        platforms: [...template.platforms],
        category: template.category,
        mediaUrl: template.mediaUrl || ''
      });
    } else {
      setEditingTemplate(null);
      setFormData({ name: '', content: '', platforms: ['facebook'], category: 'Promotions', mediaUrl: '' });
    }
    setIsModalOpen(true);
  };

  const handleSave = () => {
    const newTemplate = {
      id: editingTemplate ? editingTemplate.id : Date.now().toString(),
      ...formData,
      createdAt: editingTemplate ? editingTemplate.createdAt : new Date().toISOString()
    };

    if (editingTemplate) {
      setTemplates(templates.map(t => t.id === newTemplate.id ? newTemplate : t));
    } else {
      setTemplates([newTemplate, ...templates]);
    }
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      setTemplates(templates.filter(t => t.id !== id));
    }
  };

  const togglePlatform = (platform) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform) 
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const filteredTemplates = templates.filter(t => categoryFilter === 'All' || t.category === categoryFilter);

  const PlatformIcon = ({ type, size=16, className="" }) => {
    switch(type) {
      case 'facebook': return <Facebook size={size} className={className || "text-blue-600"} />;
      case 'instagram': return <Instagram size={size} className={className || "text-pink-600"} />;
      case 'twitter': return <Twitter size={size} className={className || "text-blue-400"} />;
      case 'linkedin': return <Linkedin size={size} className={className || "text-blue-700"} />;
      default: return null;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen bg-gray-50/50">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-pink-600 flex items-center gap-3">
            <LayoutTemplate size={32} className="text-purple-600" />
            Post Templates
          </h1>
          <p className="text-gray-500 mt-1">Save time with reusable content blocks</p>
        </div>
        <button 
          onClick={() => openModal()}
          className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all font-medium"
        >
          <Plus size={18} />
          <span>Create Template</span>
        </button>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 hide-scrollbar">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border
              ${categoryFilter === cat 
                ? 'bg-purple-50 border-purple-200 text-purple-700 shadow-sm' 
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }
            `}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Content Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-2xl h-64 border border-gray-100 p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-6"></div>
              <div className="h-20 bg-gray-200 rounded-xl mb-4"></div>
            </div>
          ))}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-12 flex flex-col items-center justify-center text-center">
          <div className="w-24 h-24 bg-purple-50 rounded-full flex items-center justify-center mb-6">
            <LayoutTemplate size={48} className="text-purple-300" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No templates found</h2>
          <p className="text-gray-500 max-w-md mx-auto mb-8">
            Create reusable post templates to save time and maintain a consistent voice across your social media marketing.
          </p>
          <button 
            onClick={() => openModal()}
            className="flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-xl hover:bg-gray-800 transition-colors font-medium shadow-md"
          >
            <Plus size={18} />
            Create First Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map(template => (
            <div key={template.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all group duration-300 flex flex-col h-full hover:-translate-y-1">
              {/* Card Header */}
              <div className="p-5 pb-3 flex justify-between items-start">
                <div>
                  <span className="text-[10px] font-bold tracking-wider uppercase text-purple-600 bg-purple-50 px-2 py-1 rounded-md mb-2 inline-block">
                    {template.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 leading-tight">{template.name}</h3>
                </div>
                <div className="flex gap-1">
                  {template.platforms.map(p => (
                    <div key={p} className="p-1 bg-gray-50 rounded-md border border-gray-100">
                      <PlatformIcon type={p} size={14} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Card Body */}
              <div className="p-5 pt-0 flex-1 flex flex-col">
                <div className="bg-gray-50 rounded-xl p-4 mb-4 flex-1 border border-gray-100 text-sm text-gray-700 italic relative overflow-hidden group-hover:bg-white transition-colors">
                  <div className="absolute top-2 left-2 text-gray-300">"</div>
                  <p className="line-clamp-4 relative z-10 pl-2">{template.content}</p>
                </div>

                {template.mediaUrl && (
                  <div className="h-24 w-full rounded-xl overflow-hidden mb-4 relative">
                    <img src={template.mediaUrl} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/10"></div>
                  </div>
                )}
                
                <div className="text-xs text-gray-400 mt-auto">
                  Created {new Date(template.createdAt).toLocaleDateString()}
                </div>
              </div>

              {/* Actions Footer */}
              <div className="grid grid-cols-3 border-t border-gray-100 bg-gray-50/50 opacity-90 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={() => navigate(`/admin/social-media/compose?template=${template.id}`)}
                  className="py-3 flex justify-center items-center gap-2 text-sm font-medium text-purple-600 hover:bg-purple-50 transition-colors border-r border-gray-100"
                >
                  <Copy size={16} /> Use
                </button>
                <button 
                  onClick={() => openModal(template)}
                  className="py-3 flex justify-center items-center gap-2 text-sm font-medium text-gray-600 hover:bg-gray-200 transition-colors border-r border-gray-100"
                >
                  <Edit2 size={16} /> Edit
                </button>
                <button 
                  onClick={() => handleDelete(template.id)}
                  className="py-3 flex justify-center items-center gap-2 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl z-10 overflow-hidden flex flex-col max-h-[90vh]">
            
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h2 className="text-xl font-bold text-gray-900">{editingTemplate ? 'Edit Template' : 'Create New Template'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 text-gray-400 hover:bg-gray-200 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Template Name</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Weekend Happy Hour"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-purple-500 outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Category</label>
                  <select 
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-purple-500 outline-none transition-all appearance-none cursor-pointer"
                  >
                    {categories.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Default Platforms</label>
                  <div className="flex gap-2">
                    {['facebook', 'instagram', 'twitter', 'linkedin'].map(p => (
                      <button
                        key={p}
                        onClick={() => togglePlatform(p)}
                        className={`p-2 rounded-lg border transition-all ${formData.platforms.includes(p) ? 'bg-purple-50 border-purple-200 shadow-sm' : 'bg-gray-50 border-transparent opacity-60 hover:opacity-100'}`}
                        title={p}
                      >
                        <PlatformIcon type={p} size={18} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Post Content</label>
                <textarea 
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder="Write your template content here..."
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-purple-500 outline-none transition-all min-h-[150px] resize-y"
                ></textarea>
                <div className="flex justify-between items-center mt-1.5 px-1">
                  <span className="text-xs text-gray-500">Supports emojis and standard text formatting.</span>
                  <span className="text-xs font-mono text-gray-400">{formData.content.length} chars</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Media Attachment (Optional)</label>
                {formData.mediaUrl ? (
                  <div className="relative rounded-xl overflow-hidden h-40 border border-gray-200 group">
                    <img src={formData.mediaUrl} alt="Template media" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button onClick={() => setFormData({...formData, mediaUrl: ''})} className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition-colors">
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:bg-gray-50 transition-colors cursor-pointer group">
                    <ImageIcon size={32} className="mx-auto text-gray-400 group-hover:text-purple-500 mb-3 transition-colors" />
                    <p className="text-sm font-medium text-gray-700">Click to upload or drag & drop</p>
                    <p className="text-xs text-gray-500 mt-1">SVG, PNG, JPG or GIF (max. 5MB)</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3">
              <button 
                onClick={() => setIsModalOpen(false)}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSave}
                disabled={!formData.name || !formData.content}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all disabled:opacity-50 disabled:hover:shadow-none"
              >
                <Save size={16} />
                Save Template
              </button>
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaTemplates;
